import * as React from 'react';
import * as Sentry from '@sentry/browser';
import { translate } from 'react-i18next';
import { Button } from '@bytebrand/fe-ui-core-autobahn/ui';
/**
 * Scoped error boundary for the autobahn (`-next`) route trees (SRP/VDP/checkout-next/
 * Group-B `variant='next'` pages). `App.componentDidCatch` (App.tsx) only reports to
 * Sentry — it has no fallback UI, so a render throw anywhere under it takes down the
 * whole app shell with a blank screen. This boundary is wired in ADDITIONALLY, closer
 * to the -next page trees (see `PrivateRoute.tsx` and `AppRouter.tsx`), so a crash in
 * one -next page shows a recoverable fallback instead. Legacy (non -next) routes are
 * untouched and still fall through to `App.componentDidCatch`.
 *
 * Sentry captures are tagged `variant: 'next'` so autobahn crashes are distinguishable
 * from legacy ones in the same Sentry project (Phase 6 wants this tag on every -next
 * capture; this boundary is the one place that can guarantee it).
 */
class AutobahnErrorBoundary extends React.Component {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "state", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { hasError: false }
        });
        // Flipping hasError back to false unmounts the fallback and mounts a FRESH instance
        // of `children` in the same slot (React remounts on type change), which clears
        // whatever local component state triggered the crash.
        Object.defineProperty(this, "handleRetry", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.setState({ hasError: false });
            }
        });
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(error, errorInfo) {
        Sentry.withScope((scope) => {
            scope.setTag('variant', 'next');
            Object.keys(errorInfo || {}).forEach((key) => {
                scope.setExtra(key, errorInfo[key]);
            });
            Sentry.captureException(error);
        });
    }
    render() {
        const { t, children } = this.props;
        if (this.state.hasError) {
            return (React.createElement("div", { role: "alert", className: "md3", style: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 16,
                    padding: '64px 24px',
                    textAlign: 'center',
                    minHeight: 320,
                } },
                React.createElement("p", { className: "md-title-lg", style: { margin: 0 } }, t ? t('autobahn:errorBoundary.title') : 'Something went wrong'),
                React.createElement("p", { className: "md-body-md", style: { margin: 0, color: 'var(--md-on-surface-variant)', maxWidth: 420 } }, t ? t('autobahn:errorBoundary.message') : 'Please try reloading this page.'),
                React.createElement(Button, { variant: "filled", onClick: this.handleRetry }, t ? t('autobahn:errorBoundary.retry') : 'Retry')));
        }
        return children;
    }
}
export default translate(['autobahn'])(AutobahnErrorBoundary);
